import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = {
    key: 0,
    class: "mt-1 text-sm text-(--colors-text-body-subtle)",
    role: "status",
    "aria-live": "polite",
    "data-testid": "invite-user-modal-duplicates"
};
const _hoisted_3 = { class: "mt-6" };
const _hoisted_4 = { class: "mb-3 text-base font-semibold text-(--colors-text-heading)" };
const _hoisted_5 = { class: "sr-only" };
const _hoisted_6 = {
    key: 1,
    class: "text-sm text-(--colors-text-error)",
    role: "alert",
    "data-testid": "invite-user-modal-catalogue-error"
};
const _hoisted_7 = {
    key: 2,
    class: "text-sm text-(--colors-text-error)",
    role: "alert",
    "data-testid": "invite-user-modal-catalogue-empty"
};
const _hoisted_8 = {
    key: 3,
    class: "mt-2 text-sm text-(--colors-text-error)",
    role: "alert",
    "data-testid": "invite-user-modal-roles-error"
};
const _hoisted_9 = {
    key: 1,
    class: "mt-4 rounded-lg border border-(--border-border-error-subtle) bg-(--background-background-error) px-4 py-3 text-sm text-(--text-text-error)",
    role: "status",
    "aria-live": "polite",
    "data-testid": "invite-user-modal-failures"
};
const _hoisted_10 = {
    key: 0,
    class: "font-semibold"
};
const _hoisted_11 = { class: "mt-2 flex flex-col gap-1" };
const _hoisted_12 = {
    key: 2,
    class: "mt-3 flex items-center gap-2 rounded-lg border border-(--border-border-error-subtle) bg-(--background-background-error) px-4 py-3 text-sm text-(--text-text-error)",
    role: "alert",
    "data-testid": "invite-user-modal-server-error"
};
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, Modal, Textarea } from "@eudonet/opal";
import { useAppToast } from "@/shared/composables/useAppToast";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { useUsersAdminStore } from "../../stores/useUsersAdminStore";
import { RolesService } from "../../infrastructure/adapters/RolesService";
import { buildRoleModuleGroups } from "../../domain/entities/Role";
import { MAX_INVITE_EMAILS, parseInviteEmails } from "../../domain/entities/inviteEmails";
import UserRolesMatrix from "./UserRolesMatrix.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'InviteUserModal',
    props: {
        modelValue: { type: Boolean }
    },
    emits: ["close", "created"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const store = useUsersAdminStore();
        const { toastSuccess } = useAppToast();
        const rolesService = new RolesService();
        const titleId = useId();
        /**
         * Per-address error codes returned in the BFF envelope, plus the request-level codes that can
         * only arrive as a thrown axios error. Kept in sync with the OPUS validator and the BFF
         * controller — a code missing here degrades to the generic fallback message.
         */
        const INVITE_ERROR_OVERRIDES = {
            KeycloakUserNotFound: "accessManager.invite.errors.userNotFound",
            "User.Invite.AlreadyMember": "accessManager.invite.errors.alreadyMember",
            "User.Invite.AlreadyInvited": "accessManager.invite.errors.alreadyInvited",
            "User.Invite.EmailRequired": "accessManager.invite.errors.emailRequired",
            "User.Invite.EmailInvalid": "accessManager.invite.errors.emailInvalid",
            "User.Invite.EmailsRequired": "accessManager.invite.errors.emailsRequired",
            "User.Invite.EmailSendFailed": "accessManager.invite.errors.emailSendFailed",
            // Distinct from EmailSendFailed on purpose: nothing was persisted, so the remedy is to invite the
            // address again rather than to resend an invitation that does not exist.
            "User.Invite.NotCompleted": "accessManager.invite.errors.notCompleted",
            "User.Invite.RolesRequired": "accessManager.invite.errors.rolesRequired",
            "User.Invite.RoleNotAssignable": "accessManager.invite.errors.roleNotAssignable",
            "User.Invite.TooManyEmails": "accessManager.invite.errors.tooManyEmails",
            // Emitted by the BFF's rate-limit filter (429), not by a validator, so it carries no dotted
            // prefix. Without an entry here a throttled AccessManager falls through to the generic "failed"
            // message and reads a temporary limit as a hard failure — then retries immediately and burns
            // another permit. The remedy is to wait, so the message has to say so.
            TooManyRequests: "accessManager.invite.errors.rateLimited",
        };
        const emailsInput = ref("");
        const selectedRoles = ref([]);
        const failures = ref([]);
        const summary = ref(null);
        const serverError = ref(null);
        const submitting = ref(false);
        const submitAttempted = ref(false);
        const emailsTouched = ref(false);
        // Cached for this component instance — the role catalogue rarely changes.
        const catalogue = ref(null);
        const catalogueError = ref(false);
        // Whether a load has finished, successfully or not. Needed to tell "still loading" from "loaded and
        // there is nothing to show": a call that succeeds with an empty list otherwise renders no matrix, no
        // message, and a Send button disabled forever with nothing explaining why.
        const catalogueSettled = ref(false);
        async function loadCatalogue() {
            try {
                catalogue.value = await rolesService.getAll();
                catalogueError.value = false;
            }
            catch (error) {
                catalogueError.value = true;
                logger.error("Failed to load the role catalogue for the invite modal", error);
            }
            finally {
                catalogueSettled.value = true;
            }
        }
        // Reset on every open so a previous attempt never leaks into the next invite.
        //
        // The catalogue is loaded here rather than in onMounted: this component stays mounted with the
        // users list, so onMounted fetched it on every visit even when the modal was never opened — and,
        // worse, a single transient /roles/all failure left catalogueError true forever, with no matrix and
        // no way to invite until a full page reload. Retrying on each open makes the failure recoverable.
        watch(() => props.modelValue, (open) => {
            if (!open) {
                return;
            }
            emailsInput.value = "";
            selectedRoles.value = [];
            failures.value = [];
            summary.value = null;
            serverError.value = null;
            submitAttempted.value = false;
            emailsTouched.value = false;
            if (catalogue.value === null || catalogueError.value) {
                catalogueError.value = false;
                catalogueSettled.value = false;
                void loadCatalogue();
            }
        }, { immediate: true });
        const parsed = computed(() => parseInviteEmails(emailsInput.value));
        const roleModuleGroups = computed(() => buildRoleModuleGroups(catalogue.value ?? [], selectedRoles.value));
        // Whether the user has engaged with the form, which is what gates the "you still have to fill this
        // in" messages.
        //
        // It must NOT be submitAttempted alone. OPAL's Button renders a native <button :disabled>, and
        // handleSubmit is the only writer of submitAttempted, so while canSubmit is false the click never
        // fires and the message never appears: a first-time user with no role selected saw nothing but a
        // greyed-out button. Switching to aria-disabled would not help either, since Button suppresses the
        // emit for that too — the messages simply cannot depend on a submit that the gate itself prevents.
        // A blur on the address field is enough to know the user has started.
        const formEngaged = computed(() => emailsTouched.value || submitAttempted.value);
        // Malformed entries take priority over the cap: a typo is what the user must fix first. Gated on
        // blur (or a submit attempt) so typing the first character of an address does not immediately paint
        // "Invalid address(es): a" — the field is a computed over the raw text, unlike the single-address
        // version it replaces, which validated on blur only.
        const emailsError = computed(() => {
            if (parsed.value.malformed.length > 0 && formEngaged.value) {
                return t("accessManager.invite.errors.malformedAddresses", {
                    addresses: parsed.value.malformed.join(", "),
                });
            }
            if (parsed.value.overCap) {
                return t("accessManager.invite.errors.tooManyEmails", { max: MAX_INVITE_EMAILS });
            }
            if (formEngaged.value && parsed.value.valid.length === 0) {
                return t("accessManager.invite.errors.emailsRequired");
            }
            return undefined;
        });
        // Same gate, so the reason Send stays disabled is actually readable. An untouched form is still not
        // pre-scolded: nothing shows until the user has been in the address field.
        const rolesError = computed(() => formEngaged.value && selectedRoles.value.length === 0
            ? t("accessManager.invite.errors.rolesRequired")
            : undefined);
        const canSubmit = computed(() => parsed.value.valid.length > 0 &&
            parsed.value.malformed.length === 0 &&
            !parsed.value.overCap &&
            selectedRoles.value.length > 0);
        function toggleRole(type) {
            selectedRoles.value = selectedRoles.value.includes(type)
                ? selectedRoles.value.filter((role) => role !== type)
                : [...selectedRoles.value, type];
        }
        function failureMessage(errorCode) {
            return t(INVITE_ERROR_OVERRIDES[errorCode] ?? "accessManager.invite.errors.failed");
        }
        async function handleSubmit() {
            submitAttempted.value = true;
            if (!canSubmit.value)
                return;
            submitting.value = true;
            serverError.value = null;
            failures.value = [];
            summary.value = null;
            try {
                const result = await store.inviteUsers(parsed.value.valid, selectedRoles.value);
                // Any invitation that landed must refresh the list behind the modal, even on a partial send.
                if (result.sent.length > 0) {
                    emit("created");
                }
                if (result.failed.length === 0) {
                    toastSuccess("accessManager.toast.inviteSent");
                    emit("close");
                    return;
                }
                failures.value = result.failed;
                summary.value = t("accessManager.invite.summary", {
                    sent: result.sent.length,
                    failed: result.failed.length,
                });
                // Keep only the refused addresses: without this, a second click re-sends the ones that
                // already succeeded, which then come back as AlreadyInvited — making the retry look worse
                // than the first attempt.
                emailsInput.value = result.failed.map((failure) => failure.email).join("\n");
            }
            catch (error) {
                serverError.value = resolveApiErrorMessage(error, INVITE_ERROR_OVERRIDES, "accessManager.invite.errors.failed");
                logger.error("Failed to invite users", error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleCancel() {
            emit("close");
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": __props.modelValue,
                size: "lg",
                "footer-position": "split",
                "aria-labelledby": _unref(titleId),
                "data-testid": "invite-user-modal",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = (v) => !v && handleCancel())
            }, {
                header: _withCtx(() => [
                    _createElementVNode("span", { id: _unref(titleId) }, _toDisplayString(_ctx.$t('accessManager.invite.title')), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        "data-testid": "invite-user-modal-cancel",
                        onClick: handleCancel
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('accessManager.invite.submit'),
                        disabled: !canSubmit.value || submitting.value,
                        loading: submitting.value,
                        "data-testid": "invite-user-modal-submit",
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createVNode(_unref(Textarea), {
                        modelValue: emailsInput.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((emailsInput).value = $event)),
                        label: _ctx.$t('accessManager.invite.emails.label'),
                        placeholder: _ctx.$t('accessManager.invite.emails.placeholder'),
                        "help-text": _ctx.$t('accessManager.invite.emails.helpText', { max: _unref(MAX_INVITE_EMAILS) }),
                        error: emailsError.value,
                        required: "",
                        "full-width": "",
                        "data-testid": "invite-user-modal-emails",
                        onBlur: _cache[1] || (_cache[1] = ($event) => (emailsTouched.value = true))
                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "help-text", "error"]),
                    (parsed.value.duplicatesRemoved > 0)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_ctx.$t('accessManager.invite.duplicatesRemoved', { count: parsed.value.duplicatesRemoved })), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("section", _hoisted_3, [
                        _createElementVNode("h2", _hoisted_4, [
                            _createTextVNode(_toDisplayString(_ctx.$t('accessManager.invite.rolesTitle')) + " ", 1 /* TEXT */),
                            _cache[3] || (_cache[3] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */)),
                            _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('accessManager.invite.rolesRequiredHint')), 1 /* TEXT */)
                        ]),
                        (roleModuleGroups.value.length > 0)
                            ? (_openBlock(), _createBlock(UserRolesMatrix, {
                                key: 0,
                                groups: roleModuleGroups.value,
                                editable: "",
                                onToggle: toggleRole
                            }, null, 8 /* PROPS */, ["groups"]))
                            : (catalogueError.value)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_ctx.$t('accessManager.invite.errors.rolesUnavailable')), 1 /* TEXT */))
                                : (catalogueSettled.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$t('accessManager.invite.errors.rolesEmpty')), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true),
                        (rolesError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(rolesError.value), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ]),
                    (failures.value.length > 0 || summary.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            (summary.value)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(summary.value), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true),
                            _createElementVNode("ul", _hoisted_11, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(failures.value, (failure) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                        key: failure.email
                                    }, _toDisplayString(failure.email) + " — " + _toDisplayString(failureMessage(failure.errorCode)), 1 /* TEXT */));
                                }), 128 /* KEYED_FRAGMENT */))
                            ])
                        ]))
                        : _createCommentVNode("v-if", true),
                    (serverError.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            _cache[4] || (_cache[4] = _createElementVNode("i", {
                                class: "ri-error-warning-line shrink-0 text-base",
                                "aria-hidden": "true"
                            }, null, -1 /* CACHED */)),
                            _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                        ]))
                        : _createCommentVNode("v-if", true)
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value", "aria-labelledby"]));
        };
    }
});
